import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { LocationEntity } from './location.entity';
// Type-only import — the runtime relation target is the string 'SensorDeviceEntity'
// so this does NOT create a runtime import cycle (sensor-device.entity imports
// SensorStatus from here as a value).
import type { SensorDeviceEntity } from './sensor-device.entity';

export enum SensorType {
	PROXIMITY_LEVEL = 'proximity_level',
	TEMPERATURE = 'temperature',
	HUMIDITY = 'humidity',
}

export enum SensorStatus {
	ONLINE = 'online',
	OFFLINE = 'offline',
	ERROR = 'error',
	MAINTENANCE = 'maintenance',
}

@Index('sensor_device_module_uindex', ['deviceId', 'moduleId'], {
	unique: true,
})
@Index('sensor_location_idx', ['locationId'])
@Entity('sensor')
export class SensorEntity extends BaseEntity {
	@Column('varchar', { name: 'device_id', length: 128 })
	public deviceId!: string;

	@Column('smallint', {
		name: 'module_id',
		unsigned: true,
		default: 1,
	})
	public moduleId!: number;

	@Column('varchar', { name: 'name', nullable: true, length: 255 })
	public name!: string | null;

	@Column('enum', {
		name: 'type',
		enum: SensorType,
		default: SensorType.PROXIMITY_LEVEL,
	})
	public type!: SensorType;

	@Column('int', {
		name: 'fk_location__id',
		unsigned: true,
		nullable: true,
	})
	public locationId!: number | null;

	/**
	 * PD-13928 — the parent sensor device (array unit) this module belongs to.
	 * Set automatically on ingestion (matched by `deviceId`). Device-level
	 * fields (status, lastSeen, firmware, …) live on SensorDeviceEntity now.
	 */
	@Column('int', {
		name: 'fk_sensor_device__id',
		unsigned: true,
		nullable: true,
	})
	public sensorDeviceId!: number | null;

	// PD-13928 — device-level health/identity (status, powerOn, lastSeen,
	// lastErrorMessage, firmwareVersion, hardwareModel) moved to
	// SensorDeviceEntity: the whole array unit shares them. The columns are
	// dropped from `sensor` by the config-api migration
	// 1780873xxxxxx-DropSensorDeviceLevelColumns. Read them via the sensor's
	// device (fkSensorDevice); the sensors API flattens them onto the response.

	// --- Calibration ---
	// PD-13924: a sensor maps to a LOCATION only — not an item. The location
	// holds the single item whose quantity the sensor's fill predicts. The
	// fk_item__id column is dropped by 1780879731852-DropSensorItemColumn.

	// Distance reading (mm) when the zone is completely empty (sensor to floor).
	// This is the maximum distance the sensor will report and represents 0% fill.
	@Column('int', { name: 'zone_depth_mm', nullable: true })
	public zoneDepthMm!: number | null;

	// Distance reading (mm) when the zone is considered full.
	// Accounts for sensor offset from the top of the zone. Default 0 means
	// product reaches the sensor when full. Set higher if there's a gap.
	@Column('int', { name: 'min_distance_mm', nullable: true, default: 0 })
	public minDistanceMm!: number | null;

	// --- Relations ---

	@ManyToOne(() => LocationEntity)
	@JoinColumn({ name: 'fk_location__id' })
	public fkLocation!: LocationEntity;

	// PD-13928 — the parent device that carries this module's device-level
	// health (status/lastSeen/firmware/…). String target avoids a runtime
	// import cycle with sensor-device.entity.
	@ManyToOne('SensorDeviceEntity')
	@JoinColumn({ name: 'fk_sensor_device__id' })
	public fkSensorDevice!: SensorDeviceEntity | null;
}
